import React from 'react';
import SafeIcon from '../common/SafeIcon';

const Sidebar = ({ 
  sections, 
  currentStep, 
  onStepClick, 
  isOpen, 
  onClose, 
  formData, 
  session, 
  onSaveProgress,
  onViewHistory,
  activeView
}) => {
  // Calculate completion for each section
  const getSectionProgress = (section) => {
    if (!section.fields || section.fields.length === 0) return 100;
    const sectionFields = section.fields.map(f => f.name);
    const filledFields = sectionFields.filter(fieldName => 
      formData[fieldName] !== undefined && 
      formData[fieldName] !== null && 
      formData[fieldName].toString().trim() !== ''
    );
    return Math.round((filledFields.length / sectionFields.length) * 100);
  };

  const totalProgress = sections.length === 0 ? 0 : Math.round(
    (sections.reduce((acc, section) => {
      if (!section.fields || section.fields.length === 0) return acc + 1;
      const sectionFields = section.fields.map(f => f.name);
      const filled = sectionFields.filter(name => 
        formData[name] !== undefined && 
        formData[name] !== null && 
        formData[name].toString().trim() !== ''
      );
      return acc + (filled.length / sectionFields.length);
    }, 0) / sections.length) * 100
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 lg:hidden" 
          onClick={onClose}
        />
      )}

      {/* Sidebar Container */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 text-slate-300 transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:w-80 flex-shrink-0 flex flex-col h-full border-r border-slate-800
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 lg:p-8 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <SafeIcon name="Compass" className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-white tracking-tight">Portal</h1>
          </div>
          <button onClick={onClose} className="lg:hidden p-2 text-slate-400 hover:text-white">
            <SafeIcon name="X" className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {session && (
            <button 
              onClick={() => {
                onViewHistory();
                onClose();
              }}
              className={`w-full group flex items-center gap-3 px-4 py-4 mb-4 rounded-2xl text-left transition-all duration-200 ${activeView === 'history' ? 'bg-white/10 text-white' : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'}`}
            >
              <SafeIcon name="History" className={`w-5 h-5 ${activeView === 'history' ? 'text-blue-400' : 'text-slate-600'}`} />
              <span className="text-sm font-black uppercase tracking-widest text-[10px]">My History</span>
            </button>
          )}

          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-600 px-4 mb-2">Questionnaire</div>
          
          {sections.map((section, idx) => {
            const isActive = activeView === 'form' && currentStep === idx;
            const progress = getSectionProgress(section);
            const isCompleted = progress === 100;

            return (
              <button
                key={section.id || idx}
                onClick={() => {
                  onStepClick(idx);
                  onClose();
                }}
                className={`w-full group flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-200 ${isActive ? 'bg-blue-600/10 text-blue-400 font-medium' : 'text-slate-500 hover:bg-slate-800/50 hover:text-slate-300'}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border flex-shrink-0 transition-all ${isActive ? 'border-blue-500/30 bg-blue-500/20 text-blue-400' : isCompleted ? 'border-emerald-500/30 bg-emerald-500/20 text-emerald-400' : 'border-slate-700 bg-slate-800/50 text-slate-500'}`}>
                  {isCompleted ? (
                    <SafeIcon name="Check" className="w-4 h-4" />
                  ) : (
                    <span className="text-[10px] font-bold">{progress}%</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate font-medium">{idx + 1}. {section.title}</div>
                  {isActive && (
                    <div className="h-0.5 w-full bg-slate-800 rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${progress}%` }} />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </nav>

        <div className="p-6 border-t border-slate-800 bg-slate-900/50">
          <div className="space-y-4">
            {!session && (
              <button 
                onClick={onSaveProgress}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-900/40 mb-4"
              >
                <SafeIcon name="Save" className="w-4 h-4" />
                Save Progress
              </button>
            )}
            <div className="flex justify-between items-center text-xs font-semibold uppercase tracking-wider text-slate-500">
              <span>Overall Progress</span>
              <span className="text-blue-400">{totalProgress}%</span>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-700 shadow-[0_0_8px_rgba(59,130,246,0.5)]" 
                style={{ width: `${totalProgress}%` }} 
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;